import moment from "moment";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { getDestinationByCode } from "shared/data/actions/destinations";
import {
  startUniversalLoading,
  stopUniversalLoading,
} from "shared/data/actions/loader";
import { AppState } from "shared/data/reducers";
import {
  ERROR_NO_DATES_AVAILABLE,
  useBestOffers,
  useListBestOffers,
} from "shared/hooks/bestOffers";
import { countPassengers } from "shared/lib/functions";
import { setInitialRequestorConfig } from "shared/lib/requestorFunctions";
import { formattedMessageParams } from "shared/messages";
import Logger from "shared/services/Logger";
import { SpecialOfferParameters, SpecialOfferType } from "shared/types";

import BackToHomepageLink from "@components/BestOffer/BackToHomepageLink";
import BestOfferItinerary from "@components/BestOffer/BestOfferItinerary";
import BestOfferPassengers from "@components/BestOffer/BestOfferPassengers";
import TripDateCalendar from "@components/Calendar/TripDateCalendar";
import { Button, PriceFooter, RouteSummary } from "@components/DesignSystem";
import FormattedMessage from "@components/UI/FormattedMessage";
import StandardLayout from "@components/UI/Layouts/StandardLayout";
import useModalAlert from "@components/UI/useModalAlert";
import PageSeo from "@components/Utils/PageSeo";

import { pageDataLayer } from "@lib/dataLayers";

const {
  arrival,
  flightNotAvailable,
  pickBothDates,
  priceFromPrefix,
  returnBeforeDepartureDate,
  specialOfferFlightNotAvailable,
  title,
} = defineMessages({
  arrival: formattedMessageParams("FlightAdditionalInfo.return"),
  flightNotAvailable: formattedMessageParams("BestOffers.flightNotAvailable"),
  pickBothDates: formattedMessageParams("BestOffers.pickBothDates"),
  priceFromPrefix: formattedMessageParams("BestOffers.priceFrom"),
  returnBeforeDepartureDate: formattedMessageParams(
    "Mobile.Alert.returnBeforeDepartureDate"
  ),
  specialOfferFlightNotAvailable: formattedMessageParams(
    "Alert.specialOfferFlightNotAvailable"
  ),
  title: formattedMessageParams("BestOffers.title"),
});

const dedupeCalendarDates = (...dateGroups: Date[][]): Date[] => {
  const datesByDay = new Map<string, Date>();

  dateGroups.flat().forEach((date) => {
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      datesByDay.set(moment(date).format("YYYY-MM-DD"), date);
    }
  });

  return Array.from(datesByDay.values());
};

const hasCalendarDate = (dates: Date[], date: Date): boolean =>
  dates.some((calendarDate) => moment(calendarDate).isSame(date, "day"));

const getValidCalendarDates = (...dates: unknown[]): Date[] =>
  dates.filter(
    (date): date is Date =>
      date instanceof Date && !Number.isNaN(date.getTime())
  );

const SpecialOffers = () => {
  const router = useRouter();
  const { query } = router;
  const bestOffersQuery = query as { [key: string]: string } & {
    dateFrom0: string;
    dateTo0: string;
    dateFrom1: string;
    dateTo1: string;
  };
  const intl = useIntl();
  const dispatch = useDispatch();
  const language = useSelector(
    (state: AppState) => state.requestorConfig.currentLanguage
  );
  const [ModalAlert, showError] = useModalAlert({});

  const {
    id,
    dateFrom0,
    dateTo0,
    dateTo1,
    lang,
    origin,
    destination,
    type,
    summaryPrice,
    alternativeCurrencySummaryPrice,
    currency: queryCurrency,
  } = bestOffersQuery;

  const [submitted, setSubmitted] = useState(false);
  const [hoveredReturnDate, setHoveredReturnDate] = useState<Date | null>(null);

  const adjustUrlCallback = (correctedDatesObj) => {
    router.push(
      {
        pathname: "/bestOffers",
        query: { ...query, ...correctedDatesObj },
      },
      undefined,
      { shallow: true }
    );
  };

  const {
    departureDates,
    returnDates,
    selectedDepartureDate,
    setSelectedDepartureDate,
    selectedReturnDate,
    setSelectedReturnDate,
    reference,
    error,
    setError,
    passengers,
    addPassenger,
    removePassenger,
    getDepartureDates,
    getReturnDates,
    onCalendarChange,
    onCalendarReturnChange,
    checkSpecialOffersAvailability,
  } = useBestOffers({
    specialOfferId: id,
    query: bestOffersQuery,
    adjustUrlCallback,
  });

  const { specialOfferEnabled, defaultCurrency } = useSelector(
    (state: AppState) => ({
      defaultCurrency: state.requestorConfig.currency,
      specialOfferEnabled: state.requestorConfig.specialOfferEnabled,
    })
  );
  const { specialOffers } = useListBestOffers(
    specialOfferEnabled,
    queryCurrency
  );

  if (error === ERROR_NO_DATES_AVAILABLE) {
    showError(intl.formatMessage(specialOfferFlightNotAvailable));
    setError(false);
  }

  const setSelectedDepartureDateFnc = (date) => {
    setSelectedDepartureDate(date);
    if (selectedReturnDate && moment(selectedReturnDate).isBefore(date)) {
      setSelectedReturnDate(null);
    }
  };

  const onTripCalendarDayClick = (date) => {
    const isDepartureDate = hasCalendarDate(getDepartureDates(), date);

    if (!returnDates) {
      if (isDepartureDate) {
        setSelectedDepartureDateFnc(date);
      }
      return;
    }

    const isReturnDate = hasCalendarDate(getReturnDates(), date);

    if (!selectedDepartureDate || selectedReturnDate) {
      if (!isDepartureDate) {
        return;
      }

      setSelectedDepartureDate(date);
      setSelectedReturnDate(null);
      setHoveredReturnDate(null);
      return;
    }

    if (isReturnDate && moment(date).isAfter(selectedDepartureDate, "day")) {
      setSelectedReturnDate(date);
      setHoveredReturnDate(null);
      return;
    }

    if (isDepartureDate) {
      setSelectedDepartureDate(date);
      setSelectedReturnDate(null);
      setHoveredReturnDate(null);
    }
  };

  const selectFlight = async () => {
    if (!selectedDepartureDate || (returnDates && !selectedReturnDate)) {
      showError(intl.formatMessage(pickBothDates));
      return;
    }

    if (
      returnDates &&
      moment(selectedReturnDate).isBefore(selectedDepartureDate, "day")
    ) {
      showError(intl.formatMessage(returnBeforeDepartureDate));
      return;
    }

    dispatch(startUniversalLoading());

    await checkSpecialOffersAvailability(
      moment(selectedDepartureDate).format("YYYY-MM-DD"),
      selectedReturnDate
        ? moment(selectedReturnDate).format("YYYY-MM-DD")
        : undefined
    );
    setSubmitted(true);
  };

  useEffect(() => {
    if (!submitted) {
      return;
    }

    try {
      if (reference) {
        router.push({
          pathname: "/bestOfferResults",
          query: {
            reference,
            ...(lang ? { lang } : {}),
            ...(queryCurrency ? { currency: queryCurrency } : {}),
          },
        });
      } else {
        setSubmitted(false);
        dispatch(stopUniversalLoading());
        showError(intl.formatMessage(flightNotAvailable));
      }
    } catch (e) {
      setSubmitted(false);
      dispatch(stopUniversalLoading());

      showError(intl.formatMessage(flightNotAvailable));
      Logger.error(e);
    }
  }, [
    dispatch,
    intl,
    lang,
    queryCurrency,
    reference,
    router,
    showError,
    submitted,
  ]);

  const specialOfferParameters: SpecialOfferParameters = {
    origin,
    destination,
    type: type as SpecialOfferType,
  };

  const [originDetails, setOriginDetails] = useState<any>();
  const [destinationDetails, setDestinationDetails] = useState<any>();
  const { airportDestinations, user } = useSelector((state: AppState) => {
    return {
      airportDestinations: state.storage.airportDestinations,
      user: state.user,
    };
  });

  const findAirportDestinationByCode = useCallback(
    async (code) => {
      if (!code) {
        return null;
      }
      return getDestinationByCode(code, airportDestinations)(dispatch);
    },
    [airportDestinations, dispatch]
  );

  useEffect(() => {
    pageDataLayer("bestOffers", user);
    (async () => {
      if (
        origin !== "undefined" &&
        (!originDetails || originDetails?.$t !== origin)
      ) {
        setOriginDetails(await findAirportDestinationByCode(origin));
      }
      if (
        destination !== "undefined" &&
        (!destinationDetails || destinationDetails?.$t !== destination)
      ) {
        setDestinationDetails(await findAirportDestinationByCode(destination));
      }
    })();

    // eslint-disable-next-line
  }, []);

  const formattedRouteTitle = `${intl.formatMessage(title)} ${
    originDetails?.$t ?? ""
  }${returnDates ? ` - ${destinationDetails?.$t ?? ""}` : ""}`;
  const selectedSpecialOffer = specialOffers.find(
    (offer) => String(offer.SpecialOfferId) === String(id)
  );
  const fallbackSummaryPrice =
    queryCurrency && selectedSpecialOffer?.AlternativeCurrencySummaryPrice
      ? selectedSpecialOffer.AlternativeCurrencySummaryPrice.FullPrice
      : selectedSpecialOffer?.SummaryPrice?.FullPrice;
  const querySummaryPrice = queryCurrency
    ? alternativeCurrencySummaryPrice
    : summaryPrice;
  const footerPrice = fallbackSummaryPrice || querySummaryPrice;
  const passengersCount = countPassengers(passengers);
  const footerTotalPrice =
    footerPrice && passengersCount > 1
      ? Number(footerPrice) * passengersCount
      : null;
  const formattedPriceFromPrefix = intl.formatMessage(priceFromPrefix);
  const departureAvailableDates = getDepartureDates();
  const returnAvailableDates = getReturnDates();
  const isSelectingReturnDate =
    !!returnDates && !!selectedDepartureDate && !selectedReturnDate;
  const selectableReturnDates =
    selectedDepartureDate && !selectedReturnDate
      ? returnAvailableDates.filter((date) =>
          moment(date).isAfter(selectedDepartureDate, "day")
        )
      : [];
  const selectedCalendarDates = getValidCalendarDates(
    selectedDepartureDate,
    selectedReturnDate
  );
  const tripCalendarAvailableDates = isSelectingReturnDate
    ? dedupeCalendarDates(
        departureAvailableDates,
        selectableReturnDates,
        selectedCalendarDates
      )
    : dedupeCalendarDates(
        departureAvailableDates,
        getValidCalendarDates(selectedDepartureDate)
      );
  const highlightedCalendarDates = isSelectingReturnDate
    ? selectableReturnDates
    : [];
  const focusableCalendarDates = tripCalendarAvailableDates;
  const selectedCalendarDateLabels: Array<{ date: Date; label: string }> = [];
  const rangeEndDate =
    selectedReturnDate ||
    (isSelectingReturnDate &&
    hoveredReturnDate &&
    moment(hoveredReturnDate).isAfter(selectedDepartureDate, "day")
      ? hoveredReturnDate
      : null);
  const selectedTripRange =
    selectedDepartureDate && rangeEndDate
      ? {
          from: selectedDepartureDate as Date,
          to: rangeEndDate as Date,
        }
      : undefined;
  if (selectedDepartureDate) {
    selectedCalendarDateLabels.push({
      date: selectedDepartureDate as Date,
      label: intl.formatMessage({ id: "Flight.departure" }),
    });
  }
  if (selectedReturnDate) {
    selectedCalendarDateLabels.push({
      date: selectedReturnDate as Date,
      label: intl.formatMessage(arrival),
    });
  }

  return (
    <div className="container best-offer-page">
      <div className="special-offers-back-to-homepage-link-wrapper">
        <BackToHomepageLink />
      </div>
      <PageSeo title={{ defaultTitle: formattedRouteTitle }} />
      <h1 className="best-offer-page-heading best-offer-page-heading--calendar">
        <FormattedMessage id="HeaderLogoStripe.firstRow" />{" "}
        <strong>
          <FormattedMessage id="HeaderLogoStripe.secondRow" />
        </strong>
        .
      </h1>
      <RouteSummary id="best-offer-options" className="best-offer-options">
        <BestOfferItinerary
          offerParameters={{
            ...specialOfferParameters,
            origin: originDetails?.$t,
            destination: destinationDetails?.$t,
          }}
        />
        <BestOfferPassengers
          passengers={passengers}
          addPassenger={addPassenger}
          removePassenger={removePassenger}
          showFlightClassInToggle={false}
          centerPropertiesPanel
        />
      </RouteSummary>
      <div className="best-offer-panel">
        <section
          aria-label={intl.formatMessage(title)}
          className="best-offer-trip-calendar"
        >
          <div
            className="best-offer-calendar"
            onMouseLeave={() => setHoveredReturnDate(null)}
          >
            <TripDateCalendar
              id="trip-calendar"
              fromMonth={dateFrom0}
              numberOfMonths={2}
              toMonth={returnDates ? dateTo1 : dateTo0}
              onDayClick={onTripCalendarDayClick}
              onDayMouseEnter={(date) => {
                setHoveredReturnDate(
                  isSelectingReturnDate &&
                    hasCalendarDate(selectableReturnDates, date)
                    ? date
                    : null
                );
              }}
              selectedDate={selectedDepartureDate}
              selectedDateLabels={selectedCalendarDateLabels}
              rangeDates={selectedTripRange}
              availableDates={tripCalendarAvailableDates}
              focusableDates={focusableCalendarDates}
              highlightedDates={highlightedCalendarDates}
              locale={language}
              onCalendarChange={(date) => {
                if (isSelectingReturnDate) {
                  onCalendarReturnChange(date);
                } else {
                  onCalendarChange(date);
                }
                if (returnDates && !isSelectingReturnDate) {
                  onCalendarReturnChange(date);
                }
                setHoveredReturnDate(null);
              }}
              startingDate={departureDates?.dateFrom}
            />
          </div>
        </section>
      </div>
      <PriceFooter
        className="best-offer-promo-footer"
        action={
          <Button
            className="best-offer-confirm-dates"
            onClick={selectFlight}
            size="lg"
          >
            <FormattedMessage id="BestOffers.selectFlights" />
          </Button>
        }
        customCurrency={queryCurrency || defaultCurrency}
        price={footerPrice}
        priceLabel={<FormattedMessage id="BestOffers.perOnePassenger" />}
        pricePrefix={formattedPriceFromPrefix}
        totalLabel={
          <>
            <FormattedMessage id="BestOffers.allPassengers" />:
          </>
        }
        totalPrice={footerTotalPrice}
      />
      <ModalAlert />
    </div>
  );
};

SpecialOffers.getInitialProps = async (initialProps) => {
  try {
    const { domain } = await setInitialRequestorConfig(initialProps);
    return { domain };
  } catch (e) {
    Logger.error(`Error Initial index: ${JSON.stringify(e)}`);
    return {};
  }
};

SpecialOffers.getLayout = (page, nonce) => (
  <StandardLayout
    nonce={nonce}
    hideFooterRights
    hideSearchForm
    showCondensedHeaderLogoStripe
  >
    {page}
  </StandardLayout>
);

export default SpecialOffers;
